import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Bell, BookOpen, Briefcase, DollarSign, User, LogOut, Home, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import ProofreaderOverview from './proofreader/ProofreaderOverview';
import AvailableBooks from './proofreader/AvailableBooks';
import CurrentWork from './proofreader/CurrentWork';
import CompletedWork from './proofreader/CompletedWork';
import PaymentHistory from './proofreader/PaymentHistory';
import ProofreaderProfile from './proofreader/ProofreaderProfile';
import NotificationPanel from '../components/NotificationPanel';

const ProofreaderDashboard = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const navItems = [
    { path: '/proofreader', icon: Home, label: 'Oorsig' },
    { path: '/proofreader/available', icon: BookOpen, label: 'Beskikbare Boeke' },
    { path: '/proofreader/current', icon: Briefcase, label: 'Huidige Werk' },
    { path: '/proofreader/completed', icon: CheckCircle, label: 'Voltooide Werk' },
    { path: '/proofreader/payments', icon: DollarSign, label: 'Betaling Geskiedenis' },
    { path: '/proofreader/profile', icon: User, label: 'Profiel' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100">
      <header className="bg-white border-b-2 border-orange-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div>
              <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                DIE BIBLIOTEEK
              </h1>
              <p className="text-sm text-gray-600">Proefleser Dashboard</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="relative">
                <Button
                  data-testid="notifications-btn"
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowNotifications(!showNotifications)}
                >
                  <Bell className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-orange-500">
                      {unreadCount}
                    </Badge>
                  )}
                </Button>
                {showNotifications && (
                  <NotificationPanel
                    notifications={notifications}
                    onClose={() => setShowNotifications(false)}
                    onRefresh={fetchNotifications}
                  />
                )}
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-gray-900">{user?.voornaam} {user?.van}</p>
                <p className="text-xs text-gray-500">Proefleser</p>
              </div>
              <Button variant="outline" size="sm" onClick={logout}>
                <LogOut className="h-4 w-4 mr-2" />
                Meld Af
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row gap-8">
          <aside className="w-full md:w-64 space-y-2">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link key={item.path} to={item.path}>
                  <Button
                    variant={isActive ? 'default' : 'ghost'}
                    className={`w-full justify-start gap-3 ${
                      isActive
                        ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white'
                        : 'hover:bg-orange-100'
                    }`}
                  >
                    <item.icon className="h-5 w-5" />
                    {item.label}
                  </Button>
                </Link>
              );
            })}
          </aside>

          <main className="flex-1">
            <Routes>
              <Route index element={<ProofreaderOverview />} />
              <Route path="available" element={<AvailableBooks />} />
              <Route path="current" element={<CurrentWork />} />
              <Route path="completed" element={<CompletedWork />} />
              <Route path="payments" element={<PaymentHistory />} />
              <Route path="profile" element={<ProofreaderProfile />} />
            </Routes>
          </main>
        </div>
      </div>
    </div>
  );
};

export default ProofreaderDashboard;